/**
 * Route guard: renders its children only for an authenticated user.
 *
 * While the initial `GET /api/auth/me` is in flight a spinner is shown — not the
 * login screen — so a page reload of a deep link does not flash a login form.
 * On 401 the user is sent to /login with the attempted location in
 * `state.from`, which LoginPage uses to return them after signing in.
 */

import { Navigate, useLocation, type Location } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '../lib/auth';
import { PageSpinner } from './Spinner';

/** Shape of the router state LoginPage reads back. */
export interface FromLocationState {
  from?: Pick<Location, 'pathname' | 'search' | 'hash'>;
}

export interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): ReactNode {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageSpinner text="Anmeldung wird geprüft…" />;

  if (!user) {
    const state: FromLocationState = {
      from: { pathname: location.pathname, search: location.search, hash: location.hash },
    };
    return <Navigate to="/login" replace state={state} />;
  }

  return <>{children}</>;
}

export default RequireAuth;
